/**
 * AcDbAssocArray import-side resolution apply — Batch-8 S-ARRAYASSOC-RESOLVE.
 *
 * Extracted out of `dxfBridge.import.ts` (AW-0, docs/MASTERPLAN.md AW-0) to
 * keep that orchestrator under the CLAUDE.md 2000-line guardrail — a pure
 * move, no behaviour change. `applyAssocArrayResolution` is called exactly
 * once, from the associative-array second pass in `dxfBridge.import.ts`,
 * right after `resolveAssocArrays` (dxf-io `handleResolution.ts`) joins each
 * ARRAY entity to its OBJECTS graph.
 */
import type {
  DxfArrayRectEntity,
  ResolvedAssocArray,
} from "@dxf-ai-editor/dxf-io";

import type { ArrayRectEntity, AssocArrayParamsSnapshot } from "./model";

/**
 * Codex #251-B — producer default for an OMITTED spacing ValueParam on an
 * associative rectangular array.
 *
 * The ACDBASSOCARRAYRECTANGULARPARAMETERS record persists its parameters as
 * named ValueParams; a producer may legitimately omit a ValueParam that was
 * never changed from the runtime object's constructed default. When AutoCAD
 * reads such a record it constructs the ObjectARX parameters object first and
 * then overlays only the persisted ValueParams — the omitted ones keep the
 * CONSTRUCTOR defaults. Per the ObjectARX SDK header
 * `AcDbAssocArrayRectangularParameters.h` (verified against the 2015 SDK
 * mirror, github.com/kevinzhwl/ObjectARXCore):
 *
 *   AcDbAssocArrayRectangularParameters(double columnSpacing = 1,
 *       double rowSpacing = 1, double levelSpacing = 1, int columnCount = 1,
 *       int rowCount = 1, int levelCount = 1, double rowElevation = 1,
 *       double axesAngle = 90);
 *
 * → the producer default for column / row / level spacing is 1 drawing unit.
 * (ezdxf gives no guidance here — it round-trips AcDbAssocArray objects as
 * opaque storage and never evaluates the params.)
 *
 * This default is applied ONLY when no real value exists ANYWHERE in the
 * source (preserve-source-never-infer): the params record did not persist the
 * spacing ValueParam AND the ARRAY entity's own INSERT grid groups (44/45)
 * were ABSENT. Absence is typed, not inferred: the dxf-io parser keeps an
 * absent 44/45 as `undefined` on `DxfArrayRectEntity["geometry"]` (it no
 * longer collapses to 0 — see entityParser.ts ARRAYRECT), and that
 * pre-collapse optionality is handed to `applyAssocArrayResolution` as
 * `sourceGrid`. A real value from either source always wins — INCLUDING a
 * real explicit `44 0` / `45 0` persisted in the source, which is now
 * distinguishable from an absent group and is preserved as 0. The default
 * exists so a valid associative 2D array never imports with every column/row
 * collapsed onto the base point.
 */
const ASSOC_ARRAY_OMITTED_SPACING_DEFAULT = 1;

/**
 * Codex #251-B sibling (local review round 9, array-spec-render-arm slice) —
 * producer default for an OMITTED "AxesAngle" ValueParam on an associative
 * rectangular array. Same ObjectARX SDK constructor cited above:
 *
 *   AcDbAssocArrayRectangularParameters(double columnSpacing = 1,
 *       double rowSpacing = 1, double levelSpacing = 1, int columnCount = 1,
 *       int rowCount = 1, int levelCount = 1, double rowElevation = 1,
 *       double axesAngle = 90);
 *
 * → the producer default for AxesAngle is 90 DEGREES = π/2 RADIANS (this
 * package's associative-storage unit — S-ARRAYRECT-AXISANGLE-UNIT,
 * containers.ts `resolveArrayAxisAngleDisplayDeg`'s doc has the full
 * derivation: AxesAngle is the angle BETWEEN the row/column axes, π/2 for a
 * standard orthogonal array, NOT the array's absolute rotation).
 *
 * Without this default, an entity whose params record omitted AxesAngle
 * would fall through to `arrayRectEntitySchema`'s own `axisAngle` default of
 * `0` — a value that belongs to the UNRELATED non-associative INSERT-grid
 * DEGREES convention (DXF group 50), NOT this associative RADIANS
 * convention. A `0` landing on a genuinely-resolved associative array is
 * indistinguishable from "no rotation authored" to every downstream
 * consumer (the round-9 display/render baseline correction, cad-core's
 * bounds/grip/select math) and gets misread as "rotated -90° from neutral"
 * — Codex P2 round 9 (local review): "a STANDARD imported array with an
 * omitted AxesAngle ValueParam would display/render/grip as -90° instead of
 * the neutral 0°."
 */
const ASSOC_ARRAY_OMITTED_AXISANGLE_DEFAULT = Math.PI / 2;

/**
 * Batch-8 S-ARRAYASSOC-RESOLVE — apply a resolved AcDbAssocArray graph onto a
 * 'Rectangular Associative Array' model entity. Mutates the entity in place
 * (it is freshly built and re-validated by `drawingDocumentSchema.parse`
 * downstream). Sets the associative flag, the §9.4 typed parameters decoded
 * from the rectangular-params record, the source entity ids resolved from the
 * AcDbAssocDependency FKs, and the handle linkage to the OBJECTS graph.
 *
 * Only fields the resolver actually decoded are written — absent params keep
 * the entity's INSERT-grid scalar values (set from the entity's own group
 * codes by `toDrawingEntity`), so a partially-decoded graph never zeroes good
 * data. The ONE exception is an omitted spacing ValueParam when the source
 * entity's own grid groups 44/45 were ALSO absent: that gets the ObjectARX
 * producer default instead (Codex #251-B — see
 * `ASSOC_ARRAY_OMITTED_SPACING_DEFAULT`).
 *
 * `sourceGrid` is the entity's PRE-collapse dxf-io geometry (spacing
 * `undefined` ⇔ group 44/45 absent in the source). It is the authoritative
 * presence signal for the #251-B default: the model entity's own
 * `columnSpacing`/`rowSpacing` are required numbers and cannot distinguish a
 * real explicit `44 0` from the absence collapse.
 */
export function applyAssocArrayResolution(
  entity: ArrayRectEntity,
  resolved: ResolvedAssocArray,
  sourceGrid: DxfArrayRectEntity["geometry"] | undefined,
): void {
  entity.associative = resolved.associative;
  if (resolved.sourceEntityIds.length > 0) {
    entity.sourceEntityIds = resolved.sourceEntityIds;
  }
  // AW-0 — dirty-diff snapshot (docs/MASTERPLAN.md AW-0), built INCREMENTALLY
  // alongside each entity write below (@codex round-1 P3 fix): a field is
  // added to the snapshot ONLY in the same branch that actually resolves it
  // from the graph (the `p`-driven branches + the #251-B producer-default
  // branches). This keeps the choke point's absence-preserving contract
  // honest — a field the graph never governed (the entity's own INSERT-grid
  // fallback value, set earlier by `toDrawingEntity`) is never captured, so
  // a LATER edit to it can never be misreported as a graph-divergent AW-1
  // gap. Assigned onto the entity at the very end of this function.
  const snapshot: AssocArrayParamsSnapshot = {};
  const p = resolved.rectParams;
  if (p) {
    if (p.columnCount !== undefined) {
      entity.columnCount = p.columnCount;
      snapshot.columnCount = p.columnCount;
    }
    if (p.rowCount !== undefined) {
      entity.rowCount = p.rowCount;
      snapshot.rowCount = p.rowCount;
    }
    if (p.levelCount !== undefined) {
      entity.levelCount = p.levelCount;
      snapshot.levelCount = p.levelCount;
    }
    if (p.columnSpacing !== undefined) {
      entity.columnSpacing = p.columnSpacing;
      entity.columnTotal = p.columnSpacing * Math.max(entity.columnCount - 1, 0);
      snapshot.columnSpacing = p.columnSpacing;
    }
    if (p.rowSpacing !== undefined) {
      entity.rowSpacing = p.rowSpacing;
      entity.rowTotal = p.rowSpacing * Math.max(entity.rowCount - 1, 0);
      snapshot.rowSpacing = p.rowSpacing;
    }
    if (p.levelSpacing !== undefined) {
      entity.levelSpacing = p.levelSpacing;
      entity.levelTotal =
        p.levelSpacing * Math.max((entity.levelCount ?? 1) - 1, 0);
      snapshot.levelSpacing = p.levelSpacing;
    }
    if (p.axisAngle !== undefined) {
      // @codex P2 (local review, round-2) — the graph's AxesAngle and the
      // anchor block-reference's OWN group-50 rotation (`sourceGrid.rotation`
      // — the entity's PRE-collapse raw geometry, per this function's own
      // doc) are two ORTHOGONAL AutoCAD quantities (grid-local axis
      // orientation vs. the array's own world-space placement). The typed
      // model has ONE `axisAngle` field for both; this overwrite discards
      // whichever the anchor carried whenever the graph ALSO resolves one.
      // No fixture (real-AutoCAD or otherwise) has ever shown BOTH non-zero
      // simultaneously — `REAL_SHAPE_DXF` (dxfBridge.arrayAssoc.test.ts) has
      // a zero anchor rotation whenever AxesAngle decodes — so this is not
      // silently fixed here (inventing a second typed field + import/export/
      // cad-core/CHPROP ripple with zero evidence of the real-world shape
      // would violate preserve-source-never-infer the OTHER way). If a real
      // source ever DOES carry both, surface it instead of losing it
      // silently — a residual gap in the same spirit as
      // `computeAssocArrayClearedFields`/`AW-2`/`AW-3`.
      if (sourceGrid?.rotation !== undefined && sourceGrid.rotation !== 0) {
        console.warn(
          `[dxfBridge.arrayAssoc] ${entity.id}: anchor's own group-50 rotation ` +
            `(${sourceGrid.rotation}) discarded — the graph ALSO resolved AxesAngle ` +
            `(${p.axisAngle}), and the typed model has one axisAngle field for ` +
            "both quantities. Re-exporting this array loses the anchor's own " +
            "rotation. Known residual gap (docs/MASTERPLAN.md AW-1 follow-up).",
        );
      }
      entity.axisAngle = p.axisAngle;
      snapshot.axisAngle = p.axisAngle;
      // @codex P2 (local review, round-4) — project whether the graph
      // resolved axisAngle from the NAMED form (genuinely degrees, per the
      // AW-1 units seam) vs. the standalone positional record (unconverted,
      // unit unverified — see the schema field's own doc). Only set `true`;
      // never explicitly cleared back to `false`/undefined once resolution
      // has run — matches every other field in this function's
      // absence-preserving contract.
      if (resolved.axisAngleFromNamedForm) {
        entity.axisAngleFromNamedForm = true;
      }
    }
    if (p.rowElevation !== undefined) {
      entity.rowElevation = p.rowElevation;
      snapshot.rowElevation = p.rowElevation;
    }
    if (p.expressions[0] !== undefined) {
      entity.columnCountExpression = p.expressions[0];
    }
    if (p.expressions[1] !== undefined) {
      entity.rowCountExpression = p.expressions[1];
    }
  }
  // ── Codex #251-B — omitted-spacing producer default ──
  // The guards above only override the entity when the params record SUPPLIED
  // a spacing value. When it omitted the ValueParam (producer default) AND the
  // entity's own INSERT grid groups 44/45 were ABSENT in the source, no real
  // value exists anywhere → a valid 2D/3D associative array would import with
  // all columns/rows/levels collapsed onto the base point (the required model
  // field collapses absence to 0 at build time). Apply the ObjectARX
  // constructor default (1 drawing unit per axis — reference in
  // `ASSOC_ARRAY_OMITTED_SPACING_DEFAULT`) for exactly that case.
  //
  // Disambiguation (W2 follow-up): absence is judged on `sourceGrid` — the
  // PRE-collapse dxf-io optionality (`undefined` ⇔ group absent) — NOT on the
  // model value being 0. A 0 on the model is ambiguous: it matches both the
  // absence collapse AND a real explicit `44 0` / `45 0` persisted in the
  // source; the latter must be preserved verbatim (preserve-source-never-
  // infer), so `sourceGrid.columnSpacing === 0` (present) blocks the default.
  // The `entity.* === 0` conjunct is a defensive invariant (an absent group
  // always built a 0 model value) — it guarantees a real non-zero value is
  // never overwritten even if the presence lookup ever missed. Totals are
  // recomputed the same way as the params-supplied branches.
  if (
    p?.columnSpacing === undefined &&
    sourceGrid?.columnSpacing === undefined &&
    entity.columnSpacing === 0
  ) {
    entity.columnSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
    entity.columnTotal =
      ASSOC_ARRAY_OMITTED_SPACING_DEFAULT * Math.max(entity.columnCount - 1, 0);
    // The producer default IS a graph-governed resolution (a real absent
    // ValueParam + absent source grid, per the ObjectARX constructor
    // default) — capture it so a later edit away from it is correctly
    // flagged dirty (unlike the untouched INSERT-grid fallback case, which
    // this snapshot never captures at all).
    snapshot.columnSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
  }
  if (
    p?.rowSpacing === undefined &&
    sourceGrid?.rowSpacing === undefined &&
    entity.rowSpacing === 0
  ) {
    entity.rowSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
    entity.rowTotal =
      ASSOC_ARRAY_OMITTED_SPACING_DEFAULT * Math.max(entity.rowCount - 1, 0);
    snapshot.rowSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
  }
  // Level spacing is OPTIONAL on the model (a plain 2D array has no Z-stack).
  // Default it only when the array actually stacks levels (levelCount > 1) —
  // materialising `levelSpacing: 1` on a single-level array would invent a
  // field absent from the source with zero geometric meaning.
  if (
    p?.levelSpacing === undefined &&
    (entity.levelCount ?? 1) > 1 &&
    (entity.levelSpacing === undefined || entity.levelSpacing === 0)
  ) {
    entity.levelSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
    entity.levelTotal =
      ASSOC_ARRAY_OMITTED_SPACING_DEFAULT *
      Math.max((entity.levelCount ?? 1) - 1, 0);
    snapshot.levelSpacing = ASSOC_ARRAY_OMITTED_SPACING_DEFAULT;
  }
  // ── Codex #251-B sibling (round 9, round 9b/9c local review, reconciled
  // against AW-1 P1 round-2 at the origin/main merge) — omitted-AxesAngle
  // producer default ──
  // The `p.axisAngle !== undefined` guard above only overrides the entity
  // when the params record SUPPLIED the ValueParam. When AxesAngle is
  // unresolved (producer default, ObjectARX constructor `axesAngle = 90`)
  // AND the anchor block-ref carries NO rotation of its own
  // (`entity.axisAngle === 0` at this point — the `g.rotation ?? 0` PRE-pass
  // value, `dxfBridge.drawingToDxf.ts`'s ARRAYRECT-import branch), apply the
  // REAL ObjectARX producer default instead of leaving the schema's
  // unrelated non-associative `0`. Guarding on `entity.associative` (set
  // above from `resolved.associative`) keeps a resolved-non-associative
  // array (its DEGREES value IS its correct neutral, unrelated convention)
  // untouched.
  //
  // The `entity.axisAngle === 0` inner guard is DELIBERATE, not the
  // over-narrow mistake round 9c fixed (that round used `axisAngle === 0` as
  // a proxy for "the OUTER `p.axisAngle !== undefined` condition never
  // fired" — ambiguous because a genuinely-DECODED real `0` collides with
  // it). HERE the outer `p?.axisAngle === undefined` gate already
  // unambiguously established that the graph never resolved AxesAngle at
  // all — this INNER check answers a DIFFERENT question: does the anchor
  // itself carry a real authored rotation worth preserving? AW-1 P1 round-2
  // (`dxfBridge.arrayAssoc.test.ts`'s "preserves the anchor's OWN rotation
  // when the graph is reachable but never resolved AxesAngle") pins the
  // answer: a NON-ZERO anchor rotation (e.g. 45°) is REAL authored data
  // (preserve-source-never-infer) and must survive untouched — round 9c's
  // "stale DEGREES value gets reinterpreted as RADIANS-minus-π/2-baseline"
  // worry that originally argued for an unconditional override is now MOOT:
  // the AW-1 three-way unit discriminant (`cad-core/common.ts::
  // isArrayAxisAngleRadians`, merged after round 9c was written) already
  // classifies a no-`assocParamsSnapshot` entity as DEGREES regardless of
  // its value, so a genuine anchor rotation is read correctly either way —
  // there is no more risk of it being silently reinterpreted as radians.
  // Only a `0` (indistinguishable from "nothing was ever authored, neither
  // by the graph nor the INSERT") gets the producer-default π/2 treatment,
  // matching this exact function's OWN "defaults omitted spacing to the
  // ObjectARX constructor default 1 (never 0)" sibling test fixture
  // (`OMITTED_SPACING_DXF`, an unrotated anchor).
  //
  // Covers BOTH `p === undefined` ("an associative graph with NO decoded
  // rectParams at all" — a real resolution-failure edge, not just an omitted
  // ValueParam within an otherwise-decoded record) and `p !== undefined &&
  // p.axisAngle === undefined` (the #251-B scenario proper). The SNAPSHOT
  // stays scoped narrower, preserving the pre-existing "an associative graph
  // with NO decoded rectParams produces an EMPTY snapshot" contract (P3 bug
  // fix, `dxfBridge.arrayAssocExport.property.test.ts`): only a genuinely-
  // RESOLVED rectParams record that omitted this one ValueParam (`p !==
  // undefined`) is a graph-governed resolution worth flagging dirty later —
  // mirrors the documented precedent just above for the INSERT-grid
  // fallback value ("a field the graph never governed... is never
  // captured [in snapshot]").
  if (p?.axisAngle === undefined && entity.associative && entity.axisAngle === 0) {
    entity.axisAngle = ASSOC_ARRAY_OMITTED_AXISANGLE_DEFAULT;
    if (p !== undefined) {
      snapshot.axisAngle = ASSOC_ARRAY_OMITTED_AXISANGLE_DEFAULT;
    }
  }
  // ── S-ARRAYRECT-PARAMS-DECODE (audit §12) — base-plane normal ──
  // Decoded from the action-owned ACDBASSOCVERTEXACTIONPARAM named
  // "BaseNormal" (ObjectARX AcDbAssocArrayCommonParameters::setBasePlane;
  // verified against real AutoCAD fixtures). Absence-preserving: the model
  // field stays undefined when the graph carries no such param — never
  // default-materialized to (0,0,1). The xAxisDirection / yAxisDirection
  // companions stay UNDECODED by research result: the X axis is a
  // "RowProfile" edge geom param (no verifying real-world fixture yet), the
  // Y axis is never persisted (derived via AxesAngle). See
  // assocArrayParser.ts "Researched deferrals" — both round-trip verbatim
  // via rawGroups. (itemRotation landed in S-ARRAYRECT-ITEMS-DECODE below —
  // it lives inside the per-item relativeTransform override matrix, never
  // as a scalar.)
  if (resolved.basePlaneNormal !== undefined) {
    entity.basePlaneNormal = { ...resolved.basePlaneNormal };
  }
  // ── S-ARRAYRECT-ITEMS-DECODE (audit §12) — per-item records ──
  // The inline AcDbAssocArrayItem list decoded from the action body (rect
  // family only — the resolver gates the projection exactly like the named
  // value-param mapping). ONLY reference-verified fields are projected
  // (grammar: ODA OdDbImpAssocArrayItem::dxfInFields — full citation on the
  // dxf-io AssocArrayItem type): the 3D grid locator, the verbatim flags
  // word + decoded erased/modified booleans, the default transform in its
  // SOURCE FORM (translation point or full 4×4 matrix — never converted),
  // the per-item relative-transform OVERRIDE, the entity-provider FK and the
  // modify-body FK. Fresh deep copies — the model must not alias the parse-
  // side round-trip objects. The transport-only per-item rawGroups slice is
  // NOT projected (the OBJECTS-graph rawGroups round-trip channel already
  // carries it; the model is the typed view).
  //
  // Absence-preserving: `assocItems` stays undefined when the graph carried
  // no decodable item list (synthetic / standalone-params shapes).
  //
  // RESEARCHED DEFERRAL — itemSourceMapping: a per-item ORIGINAL-source map
  // is NOT persisted in DXF (the only per-item entity link is the
  // entityProvider block-reference; sources are action-level
  // AcDbAssocDependency FKs shared by every item). Never derived — the
  // `itemSourceMapping` home stays absent (see arrayAssoc.ts / model.ts).
  //
  // RENDER FOLLOW-UP (not this slice — Cat B): the renderer COULD skip
  // erased items + apply relativeTransform per copy — DEFUSE S7 per-item
  // transform adapter arm (fullstack-gap-closure ARRAYEDIT-FLOW notes).
  if (resolved.items !== undefined && resolved.items.length > 0) {
    entity.assocItems = resolved.items.map((it) => {
      const item: NonNullable<ArrayRectEntity["assocItems"]>[number] = {
        locator: { ...it.locator },
        flags: it.flags,
        erased: it.erased,
        modified: it.modified,
      };
      if (it.position !== undefined) item.position = { ...it.position };
      if (it.defaultTransform !== undefined) {
        item.defaultTransform = it.defaultTransform.slice();
      }
      if (it.relativeTransform !== undefined) {
        item.relativeTransform = it.relativeTransform.slice();
      }
      if (it.entityProviderHandle !== undefined) {
        item.entityProviderHandle = it.entityProviderHandle;
      }
      if (it.repBodyHandle !== undefined) {
        item.repBodyHandle = it.repBodyHandle;
      }
      return item;
    });
  }
  // Handle linkage (round-trip wiring to the OBJECTS graph).
  const handles: NonNullable<ArrayRectEntity["assocHandles"]> = {
    dependencyHandles: resolved.dependencyHandles,
  };
  if (resolved.actionBodyHandle !== undefined) {
    handles.actionBodyHandle = resolved.actionBodyHandle;
  }
  if (resolved.arrayParametersHandle !== undefined) {
    handles.arrayParametersHandle = resolved.arrayParametersHandle;
  }
  // AW-1 — the owning ACDBASSOCACTION's own handle (round-trip linkage the
  // write-back encoder needs to locate the named-value-param form).
  if (resolved.actionHandle !== undefined) {
    handles.actionHandle = resolved.actionHandle;
  }
  // @codex P2 ("Track the owning action per named field") — carry the
  // per-field split-ownership map through verbatim when the resolver
  // detected a genuine multi-action split (absent for the common
  // single-action case — see `ResolvedAssocArray.ownerHandleByField`'s doc).
  if (resolved.ownerHandleByField !== undefined) {
    handles.ownerHandleByField = { ...resolved.ownerHandleByField };
  }
  entity.assocHandles = handles;

  // AW-0 — dirty-diff snapshot (docs/MASTERPLAN.md AW-0): the `snapshot`
  // object built incrementally above (only the fields this function actually
  // resolved from the graph — @codex round-1 P3 fix) is the one the export
  // bridge's dirty-diff choke point (dxfBridge.arrayAssocExport.ts) compares
  // against the entity's CURRENT fields at export time to detect a
  // post-import mutation.
  entity.assocParamsSnapshot = snapshot;
}
